import { type TimeSlot } from "@clipboard-health/contract-worker-app-bff";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { RegularShiftBookedBottomSheet } from "@src/appV2/redesign/Shift/BookingConfirmationBottomSheet/RegularShiftBookedBottomSheet";
import { SHIFT_DISCOVERY_WORKPLACE_PROFILE_FULL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { calculateTotalPay } from "@src/appV2/redesign/utils/calculateShiftPay";
import { getShiftWorkDurationInHours } from "@src/appV2/redesign/utils/getShiftWorkDuration";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { generatePath, useHistory } from "react-router-dom";

import { type ShiftInvite } from "../types";

interface ShiftInviteRegularShiftBookedBottomSheetContainerProps {
  modalState: UseModalState;
  shiftInvite: ShiftInvite;
}

export function ShiftInviteRegularShiftBookedBottomSheetContainer(
  props: ShiftInviteRegularShiftBookedBottomSheetContainerProps
) {
  const { modalState, shiftInvite } = props;

  const history = useHistory();

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId: shiftInvite.attributes.workplaceId,
  });

  const shiftWorkDurationInHours = getShiftWorkDurationInHours(
    {
      start: shiftInvite.attributes.shiftDetails.start,
      end: shiftInvite.attributes.shiftDetails.end,
    },
    shiftInvite.attributes.facility.requiresLunchBreak ?? false,
    mandatoryBreakDurationInMinutes
  );

  const hourlyPay = formatDollarsAsUsd(shiftInvite.attributes.shiftDetails.pay ?? 0);
  const { formattedPayAmount, payAmount } = calculateTotalPay({
    hourlyPayRate: shiftInvite.attributes.shiftDetails.pay ?? 0,
    hoursWorked: shiftWorkDurationInHours,
  });
  const { data: qualificationDisplayLabels, isLoading: isLoadingQualification } =
    useGetQualificationDisplayLabels([shiftInvite.attributes.shiftDetails.agentReq]);
  // eslint-disable-next-line prefer-destructuring
  const qualificationText = qualificationDisplayLabels[0];

  return (
    <RegularShiftBookedBottomSheet
      modalState={modalState}
      shiftStartIso={shiftInvite.attributes.shiftDetails.start}
      shiftEndIso={shiftInvite.attributes.shiftDetails.end}
      shiftTimeSlot={shiftInvite.attributes.shiftDetails.name as TimeSlot}
      workplaceName={shiftInvite.attributes.facility.name}
      workplaceTimezone={shiftInvite.attributes.facility.tmz}
      shiftHourlyPay={hourlyPay}
      shiftFormattedTotalPay={formattedPayAmount}
      shiftTotalPay={payAmount}
      shiftBoost={undefined}
      requiresBreak={shiftInvite.attributes.facility.requiresLunchBreak}
      breakDurationInMinutes={mandatoryBreakDurationInMinutes}
      isLoadingQualification={isLoadingQualification}
      qualificationText={qualificationText}
      workplaceActionLabel="See workplace profile"
      isWorkplaceActionLoading={false}
      onWorkplaceActionClick={() => {
        logEvent(APP_V2_USER_EVENTS.WORKPLACE_PROFILE_OPENED, {
          workplaceId: shiftInvite.attributes.workplaceId,
          trigger: "Shift Invite Regular Shift Booked Bottom Sheet",
        });
        const path = generatePath(SHIFT_DISCOVERY_WORKPLACE_PROFILE_FULL_PATH, {
          workplaceId: shiftInvite.attributes.workplaceId,
        });
        modalState.closeModal();
        history.push(path);
      }}
    />
  );
}
